import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, Gauge, Pencil, Image as ImageIcon, Upload, Trash2, Mail, AlertOctagon, Undo2, FileText } from "lucide-react";
import { formatSeconds } from "@/lib/tickets";

export default function AdminCenter() {
    const [policies, setPolicies] = useState(null);
    const [metric, setMetric] = useState(null);
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState([]);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [branding, setBranding] = useState(null);
    const [brandingForm, setBrandingForm] = useState({ company_name: "", tagline: "", signature_template: "", public_url: "" });
    const [savingBranding, setSavingBranding] = useState(false);
    const [logoBust, setLogoBust] = useState(Date.now());
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);
    const [reportResult, setReportResult] = useState(null);
    const [frontendHealth, setFrontendHealth] = useState(null);
    const [trash, setTrash] = useState([]);
    const [trashLoading, setTrashLoading] = useState(false);

    const loadTrash = async () => {
        setTrashLoading(true);
        try {
            const { data } = await api.get("/attachments/trash");
            setTrash(data);
        } catch (err) { setError(formatApiError(err)); }
        finally { setTrashLoading(false); }
    };

    const restoreAttachment = async (a) => {
        try {
            await api.post(`/attachments/${a.attachment_id}/restore`);
            setOk(`Adjunto "${a.filename}" restaurado.`);
            await loadTrash();
        } catch (err) { setError(formatApiError(err)); }
    };

    useEffect(() => {
        (async () => {
            try {
                const [p, m, u, b, h] = await Promise.all([
                    api.get("/sla/policies"),
                    api.get("/metrics/first-response"),
                    api.get("/users"),
                    api.get("/settings/branding/preview"),
                    api.get("/health/frontend").catch(() => ({ data: null })),
                ]);
                setPolicies(p.data);
                setMetric(m.data);
                setUsers(u.data);
                setBranding(b.data);
                setBrandingForm({
                    company_name: b.data.company_name || "",
                    tagline: b.data.tagline || "",
                    signature_template: b.data.signature_template || "",
                    public_url: b.data.public_url || "",
                });
                setFrontendHealth(h.data);
                loadTrash();
            } catch (err) { setError(formatApiError(err)); }
        })();
    }, []);

    const toggle = (uid) => {
        setSelected((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
    };
    const selectAllCustomers = () => {
        const ids = users.filter((u) => u.role === "customer").map((u) => u.user_id);
        setSelected(ids);
    };
    const clear = () => setSelected([]);

    const sendNotice = async (e) => {
        e.preventDefault();
        setSending(true); setError(""); setOk("");
        try {
            const { data } = await api.post("/notices/custom", { recipient_user_ids: selected, subject, message });
            setOk(`Enviado a ${data.queued} destinatario(s).`);
            setSubject(""); setMessage(""); setSelected([]);
        } catch (err) { setError(formatApiError(err)); }
        finally { setSending(false); }
    };

    const runWeeklyReport = async () => {
        if (!window.confirm("¿Enviar ahora el reporte semanal por empresa a todos los administradores y superadministradores?")) return;
        setSendingReport(true); setError(""); setOk(""); setReportResult(null);
        try {
            const { data } = await api.post("/notices/weekly-report/run");
            setReportResult(data);
            setOk(`Reporte enviado a ${data.recipients} administrador(es). Periodo: ${data.period_label} · ${data.companies} empresas analizadas.`);
        } catch (err) { setError(formatApiError(err)); }
        finally { setSendingReport(false); }
    };

    const saveBranding = async (e) => {
        e.preventDefault();
        setSavingBranding(true); setError(""); setOk("");
        try {
            const { data } = await api.put("/settings/branding", brandingForm);
            const { data: preview } = await api.get("/settings/branding/preview");
            setBranding({ ...data, rendered_signature: preview.rendered_signature });
            setOk("Firma corporativa actualizada.");
        } catch (err) { setError(formatApiError(err)); }
        finally { setSavingBranding(false); }
    };

    const onLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingLogo(true); setError(""); setOk("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/settings/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const { data: preview } = await api.get("/settings/branding/preview");
            setBranding(preview);
            setLogoBust(Date.now());
            setOk("Logo actualizado.");
        } catch (err) { setError(err.response?.data?.detail || err.message || "Error al subir el logo"); }
        finally { setUploadingLogo(false); e.target.value = ""; }
    };

    const removeLogo = async () => {
        if (!window.confirm("¿Eliminar el logo actual?")) return;
        setError(""); setOk("");
        try {
            await api.delete("/settings/branding/logo");
            const { data: preview } = await api.get("/settings/branding/preview");
            setBranding(preview);
            setLogoBust(Date.now());
            setOk("Logo eliminado.");
        } catch (err) { setError(formatApiError(err)); }
    };

    return (
        <div data-testid="admin-center">
            <div className="page-head">
                <div>
                    <h1>Centro administrativo</h1>
                    <p>Políticas SLA, métricas globales y comunicaciones masivas.</p>
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {ok && <div className="alert alert-ok">{ok}</div>}

            {frontendHealth && (
                <div className="card card-padded" data-testid="frontend-health" style={{ marginBottom: 16, borderLeft: `3px solid ${frontendHealth.errors_24h > 0 ? "#F59E0B" : "#16A34A"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                            <AlertOctagon size={16} color={frontendHealth.errors_24h > 0 ? "#F59E0B" : "#16A34A"} />
                            Salud del frontend (últimas 24h)
                        </h3>
                        <span className="text-muted text-xs mono">
                            Generado {new Date(frontendHealth.generated_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
                        <div className="card" style={{ padding: 12 }} data-testid="fh-errors-24h">
                            <div className="text-dim text-xs mono">ERRORES 24H</div>
                            <div className="mono" style={{ fontSize: 26, fontWeight: 700, color: frontendHealth.errors_24h > 0 ? "#F59E0B" : "var(--text)" }}>{frontendHealth.errors_24h}</div>
                        </div>
                        <div className="card" style={{ padding: 12 }} data-testid="fh-distinct-sigs">
                            <div className="text-dim text-xs mono">FIRMAS ÚNICAS</div>
                            <div className="mono" style={{ fontSize: 26, fontWeight: 700 }}>{frontendHealth.distinct_signatures}</div>
                        </div>
                        <div className="card" style={{ padding: 12 }} data-testid="fh-distinct-users">
                            <div className="text-dim text-xs mono">USUARIOS AFECTADOS</div>
                            <div className="mono" style={{ fontSize: 26, fontWeight: 700 }}>{frontendHealth.distinct_users_24h}</div>
                        </div>
                        <div className="card" style={{ padding: 12 }} data-testid="fh-last-burst">
                            <div className="text-dim text-xs mono">ÚLTIMA RÁFAGA</div>
                            <div className="mono text-sm" style={{ marginTop: 4 }}>
                                {frontendHealth.last_burst_alert
                                    ? new Date(frontendHealth.last_burst_alert.sent_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })
                                    : <span className="text-muted">— ninguna —</span>}
                            </div>
                        </div>
                    </div>
                    {frontendHealth.top_signatures.length > 0 && (
                        <>
                            <div className="text-muted text-xs mono" style={{ marginBottom: 6, textTransform: "uppercase", letterSpacing: ".06em" }}>Top firmas por incidencia</div>
                            <table className="t" style={{ marginTop: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 130 }}>Firma</th>
                                        <th>Mensaje</th>
                                        <th style={{ width: 70, textAlign: "right" }}>#</th>
                                        <th style={{ width: 130 }}>Última vez</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {frontendHealth.top_signatures.map((s) => (
                                        <tr key={s.signature} data-testid={`fh-sig-${s.signature}`}>
                                            <td className="mono text-xs">{s.signature}</td>
                                            <td className="mono text-xs" style={{ color: "#F87171", maxWidth: 480, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.message}>{s.message}</td>
                                            <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{s.count}</td>
                                            <td className="mono text-xs text-muted">{new Date(s.last_seen).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                    {frontendHealth.errors_24h === 0 && (
                        <p className="text-muted text-sm" style={{ margin: 0 }}>✓ Sin errores capturados por el ErrorBoundary en las últimas 24 horas.</p>
                    )}
                </div>
            )}

            {/* Trash — soft-deleted attachments */}
            <div className="card card-padded" data-testid="attachment-trash" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                    <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                        <Trash2 size={16} color="var(--accent)" /> Papelera de adjuntos
                        <span className="badge" style={{ background: "var(--bg-2)", color: "var(--text-muted)" }}>{trash.length}</span>
                    </h3>
                    <button className="btn btn-ghost btn-sm" onClick={loadTrash} data-testid="trash-refresh">Recargar</button>
                </div>
                <p className="text-muted text-xs" style={{ marginTop: 0, marginBottom: 12 }}>Los adjuntos eliminados permanecen aquí 7 días antes de su purga definitiva. Cualquier admin/superadmin puede restaurarlos.</p>
                {trashLoading && <div style={{ textAlign: "center", padding: 20 }}><span className="loader" /></div>}
                {!trashLoading && trash.length === 0 && <p className="text-muted text-sm" style={{ margin: 0 }}>✓ Papelera vacía.</p>}
                {!trashLoading && trash.length > 0 && (
                    <div className="table-wrap">
                        <table className="t">
                            <thead>
                                <tr>
                                    <th>Archivo</th>
                                    <th style={{ width: 140 }}>Tamaño</th>
                                    <th style={{ width: 160 }}>Eliminado</th>
                                    <th style={{ width: 160 }}>Purga</th>
                                    <th style={{ width: 130, textAlign: "right" }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trash.map((a) => (
                                    <tr key={a.attachment_id} data-testid={`trash-row-${a.attachment_id}`}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <FileText size={14} color="var(--text-muted)" />
                                                <div>
                                                    <div className="text-sm" style={{ fontWeight: 600 }}>{a.filename}</div>
                                                    <div className="text-muted mono text-xs">{a.mime}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="mono text-sm">{Math.round((a.size || 0) / 1024)} KB</td>
                                        <td className="text-xs text-muted">{a.deleted_at ? new Date(a.deleted_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                                        <td className="text-xs text-muted">{a.purge_at ? new Date(a.purge_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                                        <td style={{ textAlign: "right" }}>
                                            <button className="btn btn-ghost btn-sm" onClick={() => restoreAttachment(a)} data-testid={`trash-restore-${a.attachment_id}`}>
                                                <Undo2 size={12} /> Restaurar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="row row-2" style={{ gap: 16 }}>
                {/* SLA policies */}
                <div className="card card-padded" data-testid="sla-policies">
                    <h3 style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}><Gauge size={16} color="var(--accent)" /> Políticas SLA</h3>
                    <p className="text-muted text-sm" style={{ marginTop: 0 }}>Tiempos por prioridad. Se aplican automáticamente al crear el ticket.</p>
                    {policies && (
                        <table className="t" style={{ marginTop: 10 }}>
                            <thead><tr><th>Prioridad</th><th>1ª respuesta</th><th>Resolución</th></tr></thead>
                            <tbody>
                                {Object.entries(policies.policies).map(([key, v]) => (
                                    <tr key={key} data-testid={`sla-row-${key}`}>
                                        <td><b style={{ textTransform: "capitalize" }}>{key}</b></td>
                                        <td className="mono">{formatSeconds(v.first_response_min * 60)}</td>
                                        <td className="mono">{formatSeconds(v.resolution_min * 60)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Metrics */}
                <div className="card card-padded" data-testid="first-response-metric">
                    <h3 style={{ margin: "0 0 10px" }}>Métrica — Primera respuesta</h3>
                    <p className="text-muted text-sm" style={{ marginTop: 0 }}>Tiempo entre creación del ticket y la primera respuesta pública del staff.</p>
                    {metric && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 14 }}>
                            <div className="card" style={{ padding: 14 }}>
                                <div className="text-dim text-xs mono">PROMEDIO</div>
                                <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>{metric.avg_seconds != null ? formatSeconds(metric.avg_seconds) : "—"}</div>
                            </div>
                            <div className="card" style={{ padding: 14 }}>
                                <div className="text-dim text-xs mono">MÁS RÁPIDA</div>
                                <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: "#16A34A" }}>{metric.min_seconds != null ? formatSeconds(metric.min_seconds) : "—"}</div>
                            </div>
                            <div className="card" style={{ padding: 14 }}>
                                <div className="text-dim text-xs mono">MÁS LENTA</div>
                                <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: "#F59E0B" }}>{metric.max_seconds != null ? formatSeconds(metric.max_seconds) : "—"}</div>
                            </div>
                            <div className="card" style={{ padding: 14 }}>
                                <div className="text-dim text-xs mono">TICKETS CONTADOS</div>
                                <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: "var(--accent)" }}>{metric.count}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Branding & Signature */}
            <div className="card card-padded mt-4" data-testid="branding-card">
                <h3 style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}><Pencil size={16} color="var(--accent)" /> Firma corporativa</h3>
                <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                    Esta firma se incluye automáticamente al final de cada email enviado al cliente cuando el staff responde un ticket. Usa los placeholders <code className="mono" style={{ background: "var(--bg-2)", padding: "1px 6px", borderRadius: 4 }}>{"{agent_name}"}</code>, <code className="mono" style={{ background: "var(--bg-2)", padding: "1px 6px", borderRadius: 4 }}>{"{company_name}"}</code> y <code className="mono" style={{ background: "var(--bg-2)", padding: "1px 6px", borderRadius: 4 }}>{"{tagline}"}</code>.
                </p>

                {/* Logo */}
                <div className="card mt-4" style={{ padding: 16, display: "flex", alignItems: "center", gap: 16, background: "var(--bg-0)" }} data-testid="branding-logo-block">
                    <div style={{ width: 88, height: 88, borderRadius: 10, background: "linear-gradient(135deg,#1a1a1f,#0F0F10)", border: "1px solid var(--border-strong)", display: "grid", placeItems: "center", overflow: "hidden", flexShrink: 0 }}>
                        {branding?.logo_attachment_id ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/branding/logo.bin?v=${logoBust}`} alt="Logo" style={{ maxWidth: "85%", maxHeight: "85%", objectFit: "contain" }} data-testid="branding-logo-img" />
                        ) : (
                            <ImageIcon size={28} color="var(--text-dim)" />
                        )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Logo institucional</div>
                        <div className="text-muted text-sm">PNG, JPG, WEBP o SVG · Máximo 500 KB · Aparece en la cabecera de los emails y reemplaza el círculo rojo del header.</div>
                        <div className="flex gap-2 mt-3">
                            <label className="btn btn-ghost btn-sm" style={{ cursor: uploadingLogo ? "not-allowed" : "pointer", opacity: uploadingLogo ? 0.6 : 1 }} data-testid="branding-logo-upload-label">
                                {uploadingLogo ? <span className="loader" /> : <><Upload size={13} /> {branding?.logo_attachment_id ? "Reemplazar logo" : "Subir logo"}</>}
                                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: "none" }} onChange={onLogoUpload} disabled={uploadingLogo} data-testid="branding-logo-input" />
                            </label>
                            {branding?.logo_attachment_id && (
                                <button type="button" className="btn btn-danger btn-sm" onClick={removeLogo} data-testid="branding-logo-remove"><Trash2 size={13} /> Eliminar</button>
                            )}
                        </div>
                    </div>
                </div>

                <form onSubmit={saveBranding} className="row mt-4" style={{ gap: 14 }}>
                    <div className="row row-2">
                        <div className="field">
                            <label>Nombre de la empresa</label>
                            <input className="input" value={brandingForm.company_name} onChange={(e) => setBrandingForm({ ...brandingForm, company_name: e.target.value })} maxLength={120} data-testid="branding-company" />
                        </div>
                        <div className="field">
                            <label>Tagline / lema</label>
                            <input className="input" value={brandingForm.tagline} onChange={(e) => setBrandingForm({ ...brandingForm, tagline: e.target.value })} maxLength={200} data-testid="branding-tagline" />
                        </div>
                    </div>
                    <div className="field">
                        <label>Plantilla de firma</label>
                        <textarea className="textarea" rows={4} value={brandingForm.signature_template} onChange={(e) => setBrandingForm({ ...brandingForm, signature_template: e.target.value })} maxLength={600} data-testid="branding-template" />
                    </div>

                    <div className="field">
                        <label>URL pública de la app (botones en correos)</label>
                        <input
                            className="input"
                            type="url"
                            placeholder="https://tu-dominio-de-produccion.com"
                            value={brandingForm.public_url}
                            onChange={(e) => setBrandingForm({ ...brandingForm, public_url: e.target.value })}
                            maxLength={200}
                            data-testid="branding-public-url"
                        />
                        <small className="text-muted" style={{ display: "block", marginTop: 6, fontSize: 12, lineHeight: 1.5 }}>
                            Define el dominio que aparece en los botones "Ver ticket" / "Abrir ticket" de los correos.
                            Si lo dejas vacío se usa la variable de entorno <code>APP_PUBLIC_URL</code> del backend.
                            Cambia este campo cuando muevas la app de preview a producción.
                        </small>
                    </div>

                    {branding?.rendered_signature && (
                        <div className="field">
                            <label>Vista previa con tu nombre</label>
                            <div className="card" style={{ padding: 14, background: "var(--bg-0)", borderLeft: "3px solid var(--accent)" }} data-testid="branding-preview">
                                <pre style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--text-muted)", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{branding.rendered_signature}</pre>
                            </div>
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="submit" className="btn btn-primary" disabled={savingBranding} data-testid="branding-save">
                            {savingBranding ? <span className="loader" /> : "Guardar firma"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Custom notice */}
            <div className="card card-padded mt-4" data-testid="custom-notice">
                <h3 style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}><Send size={16} color="var(--accent)" /> Aviso personalizado</h3>
                <p className="text-muted text-sm" style={{ marginTop: 0 }}>Envía un correo electrónico a uno o varios usuarios con estética institucional.</p>

                <form onSubmit={sendNotice} className="row" style={{ gap: 14, marginTop: 14 }}>
                    <div className="field">
                        <label>Destinatarios ({selected.length} seleccionado{selected.length !== 1 ? "s" : ""})</label>
                        <div className="flex gap-2 mb-3">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={selectAllCustomers} data-testid="notice-select-customers">Seleccionar todos los clientes</button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={clear}>Limpiar</button>
                        </div>
                        <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid var(--border-strong)", borderRadius: 8, padding: 8 }}>
                            {users.map((u) => (
                                <label key={u.user_id} className="flex items-center gap-2" style={{ padding: "6px 8px", borderRadius: 6, cursor: "pointer" }} onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-2)"} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                                    <input type="checkbox" checked={selected.includes(u.user_id)} onChange={() => toggle(u.user_id)} data-testid={`notice-user-${u.user_id}`} />
                                    <span style={{ fontSize: 13 }}>{u.name}</span>
                                    <span className="text-muted mono text-xs">{u.email}</span>
                                    <span className={`badge role-${u.role}`} style={{ marginLeft: "auto", fontSize: 10 }}>{u.role}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="field">
                        <label>Asunto *</label>
                        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="notice-subject" />
                    </div>
                    <div className="field">
                        <label>Mensaje *</label>
                        <textarea className="textarea" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="notice-message" />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="submit" className="btn btn-primary" disabled={sending || selected.length === 0 || !subject || !message} data-testid="notice-send">
                            {sending ? <span className="loader" /> : <><Send size={14} /> Enviar aviso</>}
                        </button>
                    </div>
                </form>
            </div>

            {/* Weekly per-company report */}
            <div className="card card-padded mt-4" data-testid="weekly-report-card">
                <h3 style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}><Mail size={16} color="var(--accent)" /> Reporte semanal por empresa</h3>
                <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                    Cada <b>lunes a las 08:00 (CDMX)</b> se envía automáticamente a todos los administradores y superadministradores un resumen por empresa: tickets abiertos, tiempo promedio de primera respuesta y % de SLA cumplido. Aquí puedes dispararlo manualmente para vista previa.
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 14 }}>
                    <div className="text-muted text-sm">
                        {reportResult ? (
                            <span data-testid="weekly-report-result">
                                Último envío manual: <b>{reportResult.recipients}</b> destinatario(s) · <b>{reportResult.companies}</b> empresas · {reportResult.period_label}
                            </span>
                        ) : (
                            <span>Aún no se ha disparado manualmente en esta sesión.</span>
                        )}
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={runWeeklyReport}
                        disabled={sendingReport}
                        data-testid="weekly-report-run"
                    >
                        {sendingReport ? <span className="loader" /> : <><Send size={14} /> Enviar ahora</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
